import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useCallback, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { isScopedSource, variantsSourceOf } from 'api/canonicalPicker'
import type { PickerRow } from 'api/canonicalPicker.types'
import ManualListNotice from 'components/AI/CanonicalPicker/ManualListNotice'
import RetryAlert from 'components/AI/CanonicalPicker/RetryAlert'
import { buildTranslate } from 'locales'
import {
  CanonicalLevel,
  PickerSliceState,
  picksOf,
  scopeOf,
  selectActiveSelection,
  selectActiveSource,
  selectCanonicalLevel,
  selectGroupConfig,
  setBottomOperator,
  setCanonicalPicks,
  sourceChoiceOf,
  startingLevel2Operator,
  switchToDefaultSource,
  toggleCanonicalPick,
} from 'slices/aiSegmentBuilder/picker'
import describeUnlabeledPicks from 'slices/aiSegmentBuilder/picker/describeUnlabeledPicks'

import CanonicalRowsSelectContainer from './CanonicalRowsSelect.container'
import copyKeyOf from './copyKeyOf'
import placeholderOf from './placeholderOf'
import SelectionVersionsContainer from './SelectionVersions.container'
import useRetry from './useRetry'
import useScopeVersions from './useScopeVersions'
import useSwitchToSpecific from './useSwitchToSpecific'

const t = buildTranslate('components.ai.picker.canonical')

type RootState = { picker: PickerSliceState }

type Props = {
  pickerId: number
  level: CanonicalLevel
  disabled: boolean
}

/**
 * One level of a canonical picker card. It starts on smart grouping and can
 * switch to the specific versions behind its picks.
 */
const CanonicalLevelContainer = ({ pickerId, level, disabled }: Props) => {
  const dispatch = useDispatch()
  const config = useSelector(
    (state: RootState) => selectGroupConfig(state, pickerId)[level]
  )
  const level1Id = useSelector(
    (state: RootState) => selectGroupConfig(state, pickerId).level1.id
  )
  const level2OperatorOptions = useSelector(
    (state: RootState) =>
      selectGroupConfig(state, pickerId).level2.operatorOptions
  )
  const activeSource = useSelector((state: RootState) =>
    selectActiveSource(state, pickerId, level)
  )
  const selection = useSelector((state: RootState) =>
    selectActiveSelection(state, pickerId, level)
  )
  const scope = useSelector(
    (state: RootState) =>
      level === 'level2'
        ? scopeOf(selectActiveSelection(state, pickerId, 'level1'))
        : null,
    shallowEqual
  )
  const hasParentPicks = useSelector(
    (state: RootState) =>
      level === 'level2' &&
      selectActiveSelection(state, pickerId, 'level1').ids.length > 0
  )
  const unnamedIds = useSelector(
    (state: RootState) =>
      selectCanonicalLevel(state, pickerId, level).unnamedIds
  )
  const choice = useMemo(() => sourceChoiceOf(config), [config])
  const { switchToSpecific, isSwitching, switchFailed, picksWithoutVersions } =
    useSwitchToSpecific(pickerId, level, choice, selection)
  const { scopeVersions, hasUnscoped, retryScope, isRetryingScope } =
    useScopeVersions(pickerId, level, choice, selection)
  const stillUnnamedIds = useMemo(
    () => unnamedIds.filter(id => id in selection.rows),
    [selection, unnamedIds]
  )
  const describeUnnamed = useCallback(async () => {
    if (!choice) return
    await dispatch(
      describeUnlabeledPicks({
        pickerId,
        level,
        source: choice.smart,
        ids: stillUnnamedIds,
        scope,
      })
    )
  }, [choice, dispatch, level, pickerId, scope, stillUnnamedIds])
  const { retry: describeAgain, isRetrying: isDescribing } =
    useRetry(describeUnnamed)

  if (!choice) return null

  const isSpecific = activeSource === choice.specific.name
  const unmatchedCount = picksOf(selection).filter(
    row => row.kind === 'unmatchedUid'
  ).length
  const source = isSpecific ? choice.specific : choice.smart
  const isMultiselect = source.selectionType === 'multiselect'
  const copyKey = copyKeyOf(config.id)
  const hasNoScope = scope?.length === 0
  // A smart pick's versions modal reads its variants, so only a level that can find them offers versions
  const canPickSpecific =
    !isSpecific && !hasNoScope && variantsSourceOf(choice.smart) !== null

  // A fixed list on level 2 lists only what level 1's picks carry, as its smart source does
  const fixedListScopeSource =
    level === 'level2' && isSpecific && isScopedSource(choice.smart)
      ? choice.smart
      : null

  const pick = (row: PickerRow) => {
    dispatch(
      isMultiselect
        ? toggleCanonicalPick({ pickerId, level, source: source.name, row })
        : setCanonicalPicks({
            pickerId,
            level,
            source: source.name,
            rows: [row],
          })
    )
    if (!(row.id in selection.rows)) scopeVersions([row])
  }

  const setPicks = (rows: PickerRow[]) => {
    dispatch(setCanonicalPicks({ pickerId, level, source: source.name, rows }))
    scopeVersions(rows)
  }

  // Clearing level 1's single pick starts level 2 over, operator included
  const clear = () => {
    setPicks([])
    if (level === 'level1' && !isMultiselect) {
      dispatch(
        setBottomOperator({
          pickerId,
          operator: startingLevel2Operator(
            level2OperatorOptions,
            undefined,
            false
          ),
        })
      )
    }
  }

  return (
    <div>
      <CanonicalRowsSelectContainer
        key={source.name}
        source={source}
        kind={isSpecific ? 'uidGroup' : 'product'}
        selection={selection}
        scope={scope}
        scopeSource={fixedListScopeSource}
        outOfScopeLabel={t(`${copyKey}.out_of_scope`)}
        placeholder={placeholderOf({
          copyKey,
          parentCopyKey: copyKeyOf(level1Id),
          hasNoScope,
          hasParentPicks,
          isSpecific,
          isMultiselect,
        })}
        disabled={disabled || isSwitching}
        onPick={pick}
        onSetPicks={setPicks}
        onClear={clear}
      />
      <div className="d-flex align-items-center flex-wrap">
        <SelectionVersionsContainer
          pickerId={pickerId}
          level={level}
          disabled={disabled}
        />
        {canPickSpecific && (
          <Button
            kind="minimal"
            size="small"
            className="p-0 mt-xs"
            disabled={disabled}
            isLoading={isSwitching}
            onClick={switchToSpecific}
          >
            {t(`${copyKey}.pick_specific`)}
          </Button>
        )}
      </div>
      {hasUnscoped && (
        <RetryAlert
          message={t(`${copyKey}.scope_error`)}
          disabled={disabled}
          isLoading={isRetryingScope}
          onRetry={retryScope}
        />
      )}
      {stillUnnamedIds.length > 0 && (
        <RetryAlert
          message={t('names_error')}
          disabled={disabled}
          isLoading={isDescribing}
          onRetry={describeAgain}
        />
      )}
      {canPickSpecific && picksWithoutVersions.length > 0 && (
        <div role="alert">
          <Text.BodyText5 color="cayenne50">
            {t('switch_without_versions', {
              names: picksWithoutVersions.join(', '),
            })}
          </Text.BodyText5>
        </div>
      )}
      {canPickSpecific && switchFailed && (
        <RetryAlert
          message={t('switch_error')}
          disabled={disabled}
          isLoading={isSwitching}
          onRetry={switchToSpecific}
        />
      )}
      {unmatchedCount > 0 && (
        <div role="status" className="mt-xs">
          <Text.BodyText5 color="cayenne50">
            {t('unmatched.level_warning', { count: unmatchedCount })}
          </Text.BodyText5>
        </div>
      )}
      {isSpecific && (
        <ManualListNotice
          disabled={disabled}
          onSwitchToSmart={() =>
            dispatch(switchToDefaultSource({ pickerId, level }))
          }
        />
      )}
    </div>
  )
}

export default CanonicalLevelContainer
